import type { ConvertRequest, ConvertResponse, WorkerLike } from './protocol'
import type { EnginePreference } from './types'
import type { Outcome } from './engines/anydoc'

interface Pending {
  request: ConvertRequest
  settle: (outcome: Outcome) => void
}

/**
 * A fixed pool of conversion workers.
 *
 * Both WASM engines run synchronously on the thread that calls them, so a
 * conversion on the main thread would freeze the UI for the whole document.
 * Every conversion therefore goes to a worker, and the pool caps how many run
 * at once — each one instantiates its own multi-megabyte WASM instance, so
 * spawning one per file would exhaust memory on a large drop.
 */
export class ConversionPool {
  private readonly workers: WorkerLike[] = []
  private readonly idle: WorkerLike[] = []
  private readonly inFlight = new Map<WorkerLike, Pending>()
  private readonly queue: Pending[] = []
  private disposed = false

  constructor(
    private readonly factory: () => WorkerLike,
    private readonly size: number = defaultPoolSize(),
  ) {}

  /** Queue a file for conversion. Resolves with an outcome; never rejects. */
  convert(
    id: string,
    name: string,
    bytes: ArrayBuffer,
    engine: EnginePreference = 'auto',
  ): Promise<Outcome> {
    if (this.disposed) {
      return Promise.resolve(disposedOutcome())
    }
    return new Promise<Outcome>((resolve) => {
      this.queue.push({ request: { id, name, bytes, engine }, settle: resolve })
      this.pump()
    })
  }

  /** Number of jobs waiting for a free worker. */
  get pending(): number {
    return this.queue.length
  }

  dispose(): void {
    this.disposed = true
    this.queue.splice(0).forEach((job) => job.settle(disposedOutcome()))
    this.inFlight.forEach((job) => job.settle(disposedOutcome()))
    this.inFlight.clear()
    this.workers.splice(0).forEach((worker) => worker.terminate())
    this.idle.length = 0
  }

  private pump(): void {
    while (this.queue.length > 0) {
      const worker = this.acquire()
      if (!worker) return

      const job = this.queue.shift()!
      this.inFlight.set(worker, job)
      // The buffer is transferred: a 100 MB file would otherwise be cloned.
      worker.postMessage(job.request, [job.request.bytes])
    }
  }

  private acquire(): WorkerLike | undefined {
    const free = this.idle.pop()
    if (free) return free
    if (this.workers.length >= this.size) return undefined

    const worker = this.factory()
    worker.addEventListener('message', (event: { data: ConvertResponse }) => {
      this.release(worker, event.data.outcome)
    })
    worker.addEventListener('error', (event: unknown) => {
      // A worker that crashed cannot be trusted with the next job, so it is
      // dropped rather than returned to the idle set.
      const job = this.inFlight.get(worker)
      this.inFlight.delete(worker)
      this.discard(worker)
      job?.settle(crashOutcome(event))
      this.pump()
    })
    this.workers.push(worker)
    return worker
  }

  private release(worker: WorkerLike, outcome: Outcome): void {
    const job = this.inFlight.get(worker)
    this.inFlight.delete(worker)
    if (!this.disposed) this.idle.push(worker)
    job?.settle(outcome)
    this.pump()
  }

  private discard(worker: WorkerLike): void {
    const at = this.workers.indexOf(worker)
    if (at >= 0) this.workers.splice(at, 1)
    const idleAt = this.idle.indexOf(worker)
    if (idleAt >= 0) this.idle.splice(idleAt, 1)
    worker.terminate()
  }
}

/**
 * Leave headroom for the UI thread. `hardwareConcurrency` counts logical cores;
 * saturating them all makes the page janky while converting a batch.
 */
export function defaultPoolSize(): number {
  const cores =
    typeof navigator !== 'undefined' && navigator.hardwareConcurrency
      ? navigator.hardwareConcurrency
      : 4
  return Math.max(1, Math.min(4, cores - 1))
}

function disposedOutcome(): Outcome {
  return {
    ok: false,
    error: { code: 'unknown', message: 'Conversion was cancelled.' },
  }
}

function crashOutcome(event: unknown): Outcome {
  const detail =
    typeof event === 'object' && event !== null && 'message' in event
      ? String((event as { message: unknown }).message)
      : ''
  return {
    ok: false,
    error: {
      code: 'unknown',
      message: detail ? `The converter crashed: ${detail}` : 'The converter crashed.',
      hint: 'The file may be far larger than the browser can handle. Try a smaller one.',
    },
  }
}
